import { DatePicker as AntDatePickerUI, Row } from 'antd';
import Text from 'components/Text';
import appointmentActions from 'features/appointment/services/actions';
import { CALENDAR_VIEW_TYPE, PATH_LOADING } from 'features/appointment/services/constants';
import appointmentSelectors from 'features/appointment/services/selectors';
import { DATE_FORMAT, DATE_FORMAT_VIEW } from 'features/appointment/utils/format';
import moment, { Moment } from 'moment';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import React, { useMemo } from 'react';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { momentTimezone } from 'utils/time';
import calendar_icon from '../v2/icons/calendar.svg';
import { PickerDateProps } from 'antd/lib/date-picker/generatePicker';
import uiActions from 'services/UI/actions';

const AntDatePicker = AntDatePickerUI.generatePicker<Moment>(momentGenerateConfig);
const getStartDate = (date: Moment | null | undefined, v2?: boolean) => {
  if (!v2) return date?.format(DATE_FORMAT_VIEW);
  const now = moment();
  if (date?.isSame(now, 'date')) {
    return `Today, ${date?.format('MM-DD-YYYY')}`;
  }
  if (date?.isSame(now.subtract(1, 'day'), 'date')) {
    return `Yesterday, ${date?.format('MM-DD-YYYY')}`;
  }
  return date?.format('ddd, MM-DD-YYYY');
};

type IDatePickerProps = PickerDateProps<Moment> & {
  v2?: boolean;
};
const AppointmentDatePicker: React.FC<IDatePickerProps> = ({ v2 }) => {
  const dispatch = useAppDispatch();
  const viewType = appointmentSelectors.getViewType();
  const params = appointmentSelectors.paramsMasterData();

  const startDate = useMemo(() => {
    return params?.date ? momentTimezone(params?.date) : null;
  }, [params]);

  const endDate = useMemo(() => {
    return params?.date ? momentTimezone(params?.date) : null;
  }, [params]);

  if (viewType === CALENDAR_VIEW_TYPE.DAY_VIEW) {
    const onChange: IDatePickerProps['onChange'] = (date) => {
      if (!date) return;
      dispatch(appointmentActions.initData(date?.format(DATE_FORMAT)));
    };

    return (
      <DatePickerStyled
        onChange={onChange}
        size='small'
        inputReadOnly
        clearIcon={false}
        value={startDate}
        suffixIcon={v2 ? false : undefined}
        className={v2 ? 'v2' : ''}
        inputRender={(props: any) => (
          <InputUI {...props}>
            {!v2 && (
              <Text variant='CONTENT_2' color='text_2' mr={2}>
                Time Period:
              </Text>
            )}
            <Text variant='CONTENT_2' color='text_3'>
              {getStartDate(startDate, v2)}
            </Text>
            {v2 && (
              <img
                src={calendar_icon}
                className='calendar_icon'
                style={{ width: 32, height: 32 }}
              />
            )}
          </InputUI>
        )}
      />
    );
  }

  if (viewType === CALENDAR_VIEW_TYPE.WEEK_VIEW) {
    const onChange: IDatePickerProps['onChange'] = (date) => {
      if (!date) return;
      dispatch(
        appointmentActions.setParams({
          startTime: date?.startOf('week').format(DATE_FORMAT),
          endTime: date?.endOf('week').format(DATE_FORMAT),
        })
      );
    };

    return (
      <DatePickerStyled
        onChange={onChange}
        picker='week'
        size='small'
        clearIcon={false}
        inputReadOnly
        value={startDate}
        suffixIcon={v2 ? false : undefined}
        className={v2 ? 'v2' : ''}
        inputRender={(props: any) => (
          <InputUI {...props}>
            {!v2 && (
              <Text variant='CONTENT_2' color='text_2' mr={2}>
                Time Period:
              </Text>
            )}
            <Text variant='CONTENT_2' color='text_3'>
              {startDate?.format(DATE_FORMAT_VIEW)} - {endDate?.format(DATE_FORMAT_VIEW)}
            </Text>
            {v2 && (
              <img
                src={calendar_icon}
                className='calendar_icon'
                style={{ width: 32, height: 32 }}
              />
            )}
          </InputUI>
        )}
      />
    );
  }

  if (viewType === CALENDAR_VIEW_TYPE.MONTH_VIEW) {
    const onChange: IDatePickerProps['onChange'] = (date) => {
      if (!date) return;
      dispatch(
        appointmentActions.setParams({
          startTime: date?.startOf('month').format(DATE_FORMAT),
          endTime: date?.endOf('month').format(DATE_FORMAT),
        })
      );
    };
    return (
      <DatePickerStyled
        clearIcon={false}
        inputReadOnly
        value={startDate}
        onChange={onChange}
        picker='month'
        suffixIcon={v2 ? false : undefined}
        className={v2 ? 'v2' : ''}
        inputRender={(props: any) => (
          <InputUI {...props}>
            {!v2 && (
              <Text variant='CONTENT_2' color='text_2' mr={2}>
                Time Period:
              </Text>
            )}
            <Text variant='CONTENT_2' color='text_3'>
              {startDate?.format('MM/YYYY')}
            </Text>
            {v2 && (
              <img
                src={calendar_icon}
                className='calendar_icon'
                style={{ width: 32, height: 32 }}
              />
            )}
          </InputUI>
        )}
        size='small'
      />
    );
  }

  return null;
};

export default AppointmentDatePicker;
type DatePickerStyledProps = {};
const DatePickerStyled = styled(AntDatePicker) <DatePickerStyledProps>`
  &.ant-picker {
    width: unset;
  }
  &.v2 {
    height: 100%;
    background: transparent;
    border: 1px solid transparent;
  }
`;

const InputUI = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  .CONTENT_2 {
    color: #505050;
    font-family: Poppins;
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    white-space: nowrap;
  }
  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none; /* Standard syntax */
`;
